import { useCallback, useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  BarChart3,
  Database,
  Globe,
  GraduationCap,
  Info,
  Network,
  RefreshCw,
  Stethoscope,
} from "lucide-react";
import { functions } from "../../firebase/config";
import {
  FIRESTORE_FREE,
  FIRESTORE_PROJECT_NOTE,
  READS_HEURISTIC,
  estimateReadsPerDay,
  fmtNum,
  pctQuota,
} from "../../lib/trafficReport";

const TABS = [
  { id: "trafico", label: "Tráfico web", icon: Network },
  { id: "website", label: "Website CRM", icon: Globe },
  { id: "academia", label: "Academia", icon: GraduationCap },
  { id: "medicina", label: "SIGMO", icon: Stethoscope },
  { id: "consolidado", label: "Consolidado", icon: Database },
];

function MetricBar({ label, value, max, tone = "bg-brand-primary" }) {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[11px] text-brand-dark/55">
        <span>{label}</span>
        <span className="font-semibold text-brand-dark">{fmtNum(value)}</span>
      </div>
      <div className="h-2 rounded-full bg-brand-soft overflow-hidden">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function QuotaBar({ pct: p }) {
  const tone = p >= 80 ? "bg-red-500" : p >= 40 ? "bg-amber-500" : "bg-brand-primary";
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[11px]">
        <span className="text-brand-dark/55">Uso estimado vs cuota gratis</span>
        <span className="font-bold text-brand-dark">{p}%</span>
      </div>
      <div className="h-2.5 rounded-full bg-brand-soft overflow-hidden">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(100, p)}%` }} />
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-brand-soft bg-[#f8faf9] px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">{label}</p>
      <p className="font-display text-2xl font-black text-brand-dark mt-1">{value}</p>
    </div>
  );
}

function ProductReport({ report, heurKey }) {
  const chartMax = useMemo(
    () => Math.max(0, ...report.monthly.map((m) => m.total)),
    [report.monthly]
  );
  const heur = READS_HEURISTIC[heurKey];
  const readsDay = estimateReadsPerDay(report.summary.eventosMes, heur.typical);
  const quota = pctQuota(readsDay);

  const summaryEntries = Object.entries(report.summary).filter(([k]) => k !== "eventosMes");

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">Summary — {report.period.label}</h3>
            <p className="text-sm text-brand-dark/55">
              {report.url} · DB <code className="text-xs">{report.firestoreDb}</code>
            </p>
          </div>
          <p className="text-[10px] text-brand-dark/40 uppercase tracking-widest">
            En vivo · Firestore
          </p>
        </div>
        {(report.period.firstActivity || report.period.lastActivity) && (
          <p className="text-xs text-brand-dark/45">
            Primera actividad: {report.period.firstActivity || "—"} · Última:{" "}
            {report.period.lastActivity || "—"}
          </p>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {summaryEntries.map(([key, val]) => (
            <SummaryCard key={key} label={key.replace(/([A-Z])/g, " $1")} value={fmtNum(val)} />
          ))}
          <SummaryCard
            label={`Eventos mes (${report.metricLabel})`}
            value={fmtNum(report.summary.eventosMes)}
          />
        </div>
        {report.note ? (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200/60 rounded-xl px-3 py-2">
            {report.note}
          </p>
        ) : null}
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Monthly history — últimos 12 meses</h3>
        <div className="space-y-3">
          {report.monthly.map((m) => (
            <MetricBar key={m.key} label={m.month} value={m.total} max={chartMax || 1} />
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[480px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-brand-dark/45 border-b border-brand-soft">
                <th className="py-2 pr-3">Mes</th>
                <th className="py-2 text-right">Total eventos</th>
              </tr>
            </thead>
            <tbody>
              {report.monthly.map((m) => (
                <tr key={m.key} className="border-b border-brand-soft/60">
                  <td className="py-2 pr-3">{m.month}</td>
                  <td className="py-2 text-right tabular-nums font-medium">{fmtNum(m.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Days of month — mes actual</h3>
        <div className="grid grid-cols-8 sm:grid-cols-12 md:grid-cols-16 gap-1">
          {report.daily.map((d) => {
            const h = Math.min(48, 8 + d.total * 6);
            return (
              <div key={d.key} className="flex flex-col items-center gap-1" title={`Día ${d.day}: ${d.total}`}>
                <div
                  className="w-full rounded-t bg-brand-primary/80 min-h-[4px]"
                  style={{ height: `${h}px` }}
                />
                <span className="text-[9px] text-brand-dark/45">{d.day}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-3">
        <h3 className="font-bold text-lg">Proyección Firestore</h3>
        <p className="text-sm text-brand-dark/55">
          ~{heur.typical} {heur.label} → <strong>{fmtNum(readsDay)} lecturas/día</strong> (estimado)
        </p>
        <QuotaBar pct={quota} />
      </section>
    </div>
  );
}

function ConsolidadoView({ data }) {
  const rows = [
    { id: "website", report: data.website, heur: READS_HEURISTIC.website },
    { id: "academia", report: data.academia, heur: READS_HEURISTIC.academia },
    { id: "medicina", report: data.medicina, heur: READS_HEURISTIC.medicina },
  ];
  const totalReads = rows.reduce(
    (n, r) => n + estimateReadsPerDay(r.report.summary.eventosMes, r.heur.typical),
    0
  );
  const quota = pctQuota(totalReads);

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Consolidado en vivo</h3>
        <p className="text-sm text-brand-dark/55">{FIRESTORE_PROJECT_NOTE}</p>
        <p className="text-xs text-brand-dark/45">
          Actualizado: {data.generatedAtLabel || data.generatedAt}
        </p>
        <div className="rounded-2xl border border-brand-soft p-4">
          <p className="text-[10px] uppercase tracking-widest text-brand-dark/45">
            Lecturas estimadas / día (todos los productos)
          </p>
          <p className="font-display text-3xl font-black text-brand-primary mt-1">
            {fmtNum(totalReads)}
          </p>
          <QuotaBar pct={quota} />
          <p className="text-xs text-brand-dark/45 mt-2">
            Cuota Spark: {fmtNum(FIRESTORE_FREE.readsPerDay)} lecturas/día
          </p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-brand-dark/45 border-b border-brand-soft">
              <th className="py-2 text-left">Producto</th>
              <th className="py-2 text-right">Eventos mes</th>
              <th className="py-2 text-right">Lect./día est.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ report, heur }) => {
              const rd = estimateReadsPerDay(report.summary.eventosMes, heur.typical);
              return (
                <tr key={report.product} className="border-b border-brand-soft/60">
                  <td className="py-2.5">{report.label}</td>
                  <td className="py-2.5 text-right tabular-nums">{fmtNum(report.summary.eventosMes)}</td>
                  <td className="py-2.5 text-right tabular-nums">{fmtNum(rd)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <div className="flex gap-2 text-sm text-brand-dark/60 bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3">
        <Info size={16} className="shrink-0 mt-0.5 text-brand-primary" />
        <p>
          Los informes reflejan <strong>actividad operativa en Firestore</strong>, no AWStats del hosting.
          Cada producto usa métricas acordes a su dominio: CRM web, LMS y clínica SIGMO.
        </p>
      </div>
    </div>
  );
}

function dayKeyLocal(d = new Date()) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/El_Salvador",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(d);
}

function shiftDayKey(dayKey, deltaDays) {
  const ms = Date.parse(`${dayKey}T12:00:00Z`) + deltaDays * 24 * 60 * 60 * 1000;
  return new Date(ms).toISOString().slice(0, 10);
}

function defaultTrafficRange() {
  const to = dayKeyLocal();
  return { from: shiftDayKey(to, -29), to };
}

function TrafficReport({ traffic, range, onRangeChange, onApply, loading }) {
  if (!traffic && !range) {
    return (
      <p className="text-sm text-brand-dark/50 py-6">
        Sin datos de tráfico aún. Las visitas se registran al navegar el sitio público (anónimas o
        con IP si aceptan analítica).
      </p>
    );
  }

  const periodLabel = traffic?.period
    ? `${traffic.period.from} → ${traffic.period.to} (${traffic.period.dayCount} días)`
    : `${range.from} → ${range.to}`;
  const chartMax = Math.max(
    1,
    ...(traffic?.daily || []).map((d) =>
      Math.max(d.visitsConsent || 0, d.visitsAnonymous || 0, d.visitsTotal || 0)
    )
  );
  const monthMax = Math.max(1, ...(traffic?.monthly || []).map((m) => m.visitsTotal || 0));
  const wc = traffic?.withConsent || {};
  const wo = traffic?.withoutConsent || {};
  const consent = traffic?.consent || {};

  const applyPreset = (preset) => {
    const to = dayKeyLocal();
    let next = range;
    if (preset === "7d") next = { from: shiftDayKey(to, -6), to };
    else if (preset === "30d") next = { from: shiftDayKey(to, -29), to };
    else if (preset === "month") next = { from: `${to.slice(0, 7)}-01`, to };
    onRangeChange(next);
    onApply(next);
  };

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h3 className="font-bold text-lg">Periodo a analizar</h3>
            <p className="text-xs text-brand-dark/55 mt-1">{periodLabel}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              ["7d", "7 días"],
              ["30d", "30 días"],
              ["month", "Mes actual"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => applyPreset(id)}
                className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-brand-soft hover:bg-brand-soft/40"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs space-y-1">
            <span className="font-bold uppercase tracking-widest text-brand-dark/45">Desde</span>
            <input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(e) => onRangeChange({ ...range, from: e.target.value })}
              className="block rounded-xl border border-brand-soft px-3 py-2 text-sm"
            />
          </label>
          <label className="text-xs space-y-1">
            <span className="font-bold uppercase tracking-widest text-brand-dark/45">Hasta</span>
            <input
              type="date"
              value={range.to}
              min={range.from}
              max={dayKeyLocal()}
              onChange={(e) => onRangeChange({ ...range, to: e.target.value })}
              className="block rounded-xl border border-brand-soft px-3 py-2 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={() => onApply(range)}
            disabled={loading}
            className="px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest bg-brand-dark text-white disabled:opacity-60"
          >
            Aplicar periodo
          </button>
        </div>
      </section>

      {!traffic ? (
        <p className="text-sm text-brand-dark/50 py-6">
          Sin datos de tráfico para este periodo todavía.
        </p>
      ) : (
        <>
          <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
            <h3 className="font-bold text-lg">Resumen del periodo</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <SummaryCard
                label="Visitas indistintas (todas)"
                value={fmtNum(traffic.combined?.visitsPeriod ?? traffic.combined?.visitasIndistintasIp)}
              />
              <SummaryCard
                label="Último día del periodo"
                value={fmtNum(traffic.combined?.visitsToday)}
              />
            </div>
          </section>

          <section className="bg-white rounded-[28px] border border-brand-primary/30 p-6 space-y-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-brand-primary">
                Declaración de privacidad / cookies
              </p>
              <h3 className="font-bold text-lg">{consent.label || "Decisiones del banner"}</h3>
              <p className="text-xs text-brand-dark/55 mt-1">{consent.note}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <SummaryCard label="Aceptaron" value={fmtNum(consent.accepted)} />
              <SummaryCard label="Rechazaron" value={fmtNum(consent.rejected)} />
              <SummaryCard label="Configuraron" value={fmtNum(consent.customized)} />
              <SummaryCard label="Total decisiones" value={fmtNum(consent.total)} />
              <SummaryCard
                label="% aceptación"
                value={consent.acceptRate == null ? "—" : `${consent.acceptRate}%`}
              />
            </div>
          </section>

          <section className="bg-white rounded-[28px] border border-brand-primary/30 p-6 space-y-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-brand-primary">
                Con consentimiento
              </p>
              <h3 className="font-bold text-lg">{wc.label || "Analítica con IP"}</h3>
              <p className="text-xs text-brand-dark/55 mt-1">{wc.note}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <SummaryCard label="Periodo — visitas" value={fmtNum(wc.visitsPeriod ?? wc.visits30)} />
              <SummaryCard
                label={`IPs distintas (últ. ${wc.ipDetailDays || 14}d del periodo)`}
                value={fmtNum(wc.uniqueIps7d)}
              />
              <SummaryCard label="Suma uniqueIps/día" value={fmtNum(wc.uniqueIpsPeriod)} />
              <SummaryCard label="Último día — visitas" value={fmtNum(wc.visitsToday)} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-brand-dark/45 border-b border-brand-soft">
                    <th className="py-2 pr-3">IP</th>
                    <th className="py-2 text-right pr-3">Visitas</th>
                    <th className="py-2">Rutas</th>
                    <th className="py-2 text-right">Última</th>
                  </tr>
                </thead>
                <tbody>
                  {(wc.byIp || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-brand-dark/45">
                        Aún no hay visitas con consentimiento de analítica.
                      </td>
                    </tr>
                  ) : (
                    wc.byIp.map((row) => (
                      <tr key={row.ip} className="border-b border-brand-soft/60 align-top">
                        <td className="py-2.5 pr-3 font-mono text-xs">{row.ip}</td>
                        <td className="py-2.5 text-right tabular-nums font-semibold pr-3">
                          {fmtNum(row.visits)}
                        </td>
                        <td className="py-2.5 text-xs text-brand-dark/65">
                          {(row.topPaths || [])
                            .map((p) => `${p.path} (${p.count})`)
                            .join(" · ") || "—"}
                        </td>
                        <td className="py-2.5 text-right text-xs text-brand-dark/45 whitespace-nowrap">
                          {row.lastSeen
                            ? new Date(row.lastSeen).toLocaleString("es-SV", {
                                timeZone: "America/El_Salvador",
                              })
                            : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">
                Sin consentimiento
              </p>
              <h3 className="font-bold text-lg">{wo.label || "Solo estadística"}</h3>
              <p className="text-xs text-brand-dark/55 mt-1">{wo.note}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <SummaryCard
                label="Periodo — visitas anónimas"
                value={fmtNum(wo.visitsPeriod ?? wo.visits30)}
              />
              <SummaryCard label="Último día — anónimas" value={fmtNum(wo.visitsToday)} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[360px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-brand-dark/45 border-b border-brand-soft">
                    <th className="py-2">Ruta (agregado, sin IP)</th>
                    <th className="py-2 text-right">Visitas</th>
                  </tr>
                </thead>
                <tbody>
                  {(wo.topPaths || []).length === 0 ? (
                    <tr>
                      <td colSpan={2} className="py-6 text-center text-brand-dark/45">
                        Sin rutas anónimas registradas aún.
                      </td>
                    </tr>
                  ) : (
                    wo.topPaths.map((row) => (
                      <tr key={row.path} className="border-b border-brand-soft/60">
                        <td className="py-2 font-mono text-xs">{row.path}</td>
                        <td className="py-2 text-right tabular-nums font-semibold">
                          {fmtNum(row.count)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {(traffic.monthly || []).length > 0 && (
            <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
              <h3 className="font-bold text-lg">Monthly history — periodo</h3>
              <div className="space-y-3">
                {traffic.monthly.map((m) => (
                  <div key={m.month} className="space-y-1">
                    <p className="text-[11px] text-brand-dark/55">
                      {m.month} · con IP {fmtNum(m.visitsConsent)} · anónimas{" "}
                      {fmtNum(m.visitsAnonymous)} · aceptaron {fmtNum(m.accepted)} · rechazaron{" "}
                      {fmtNum(m.rejected)}
                    </p>
                    <MetricBar label="Total mes" value={m.visitsTotal} max={monthMax} />
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
            <h3 className="font-bold text-lg">Days of period — desglose diario</h3>
            <div className="space-y-3">
              {(traffic.daily || []).map((d) => (
                <div key={d.date} className="space-y-1">
                  <p className="text-[11px] text-brand-dark/55">
                    {d.date} · con IP {fmtNum(d.visitsConsent)} · anónimas{" "}
                    {fmtNum(d.visitsAnonymous)}
                    {(d.accepted || d.rejected || d.customized)
                      ? ` · banner ✓${fmtNum(d.accepted)} ✗${fmtNum(d.rejected)} ⚙${fmtNum(d.customized)}`
                      : ""}
                  </p>
                  <MetricBar label="Total día" value={d.visitsTotal} max={chartMax} />
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function AcademyTrafficBlock({ traffic, range, onRangeChange, onApply, loading }) {
  const periodLabel = traffic?.period
    ? `${traffic.period.from} → ${traffic.period.to} (${traffic.period.dayCount} días)`
    : `${range.from} → ${range.to}`;
  const pub = traffic?.public || {};
  const auth = traffic?.auth || {};
  const access = traffic?.access || {};
  const chartMax = Math.max(
    1,
    ...(traffic?.daily || []).map((d) =>
      Math.max(d.visitsPublic || 0, d.uniqueAuthUsers || 0, d.visitsAuth || 0)
    )
  );

  const applyPreset = (preset) => {
    const to = dayKeyLocal();
    let next = range;
    if (preset === "7d") next = { from: shiftDayKey(to, -6), to };
    else if (preset === "30d") next = { from: shiftDayKey(to, -29), to };
    else if (preset === "month") next = { from: `${to.slice(0, 7)}-01`, to };
    onRangeChange(next);
    onApply(next);
  };

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h3 className="font-bold text-lg">Tráfico Academia — periodo</h3>
            <p className="text-xs text-brand-dark/55 mt-1">{periodLabel}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              ["7d", "7 días"],
              ["30d", "30 días"],
              ["month", "Mes actual"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => applyPreset(id)}
                className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-brand-soft hover:bg-brand-soft/40"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs space-y-1">
            <span className="font-bold uppercase tracking-widest text-brand-dark/45">Desde</span>
            <input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(e) => onRangeChange({ ...range, from: e.target.value })}
              className="block rounded-xl border border-brand-soft px-3 py-2 text-sm"
            />
          </label>
          <label className="text-xs space-y-1">
            <span className="font-bold uppercase tracking-widest text-brand-dark/45">Hasta</span>
            <input
              type="date"
              value={range.to}
              min={range.from}
              max={dayKeyLocal()}
              onChange={(e) => onRangeChange({ ...range, to: e.target.value })}
              className="block rounded-xl border border-brand-soft px-3 py-2 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={() => onApply(range)}
            disabled={loading}
            className="px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest bg-brand-dark text-white disabled:opacity-60"
          >
            Aplicar periodo
          </button>
        </div>
      </section>

      {!traffic ? (
        <p className="text-sm text-brand-dark/50 py-4">
          Sin datos de tráfico Academia aún. Se registran al visitar el catálogo público o al ingresar
          con cuenta.
        </p>
      ) : (
        <>
          <section className="bg-white rounded-[28px] border border-brand-primary/30 p-6 space-y-4">
            <h3 className="font-bold text-lg">Usuarios con acceso</h3>
            <p className="text-xs text-brand-dark/55">{access.note}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <SummaryCard label="Con acceso (activos)" value={fmtNum(access.usersWithAccess)} />
              <SummaryCard label="Aceptaron privacidad" value={fmtNum(access.privacyAccepted)} />
              <SummaryCard
                label="% privacidad"
                value={access.privacyRate == null ? "—" : `${access.privacyRate}%`}
              />
              <SummaryCard
                label="% ingresaron hoy (DAU)"
                value={auth.loginRateToday == null ? "—" : `${auth.loginRateToday}%`}
              />
            </div>
          </section>

          <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">
                Sin sesión
              </p>
              <h3 className="font-bold text-lg">{pub.label}</h3>
              <p className="text-xs text-brand-dark/55 mt-1">{pub.note}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <SummaryCard label="Visitas periodo" value={fmtNum(pub.visitsPeriod)} />
              <SummaryCard label="Último día" value={fmtNum(pub.visitsLastDay)} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[360px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-brand-dark/45 border-b border-brand-soft">
                    <th className="py-2">Ruta pública</th>
                    <th className="py-2 text-right">Visitas</th>
                  </tr>
                </thead>
                <tbody>
                  {(pub.topPaths || []).length === 0 ? (
                    <tr>
                      <td colSpan={2} className="py-6 text-center text-brand-dark/45">
                        Sin rutas públicas registradas aún.
                      </td>
                    </tr>
                  ) : (
                    pub.topPaths.map((row) => (
                      <tr key={row.path} className="border-b border-brand-soft/60">
                        <td className="py-2 font-mono text-xs">{row.path}</td>
                        <td className="py-2 text-right tabular-nums font-semibold">
                          {fmtNum(row.count)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white rounded-[28px] border border-brand-primary/30 p-6 space-y-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-brand-primary">
                Con acceso
              </p>
              <h3 className="font-bold text-lg">{auth.label}</h3>
              <p className="text-xs text-brand-dark/55 mt-1">{auth.note}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <SummaryCard label="Ingresaron (último día)" value={fmtNum(auth.uniqueLastDay)} />
              <SummaryCard label="Suma DAU del periodo" value={fmtNum(auth.uniqueSumPeriod)} />
              <SummaryCard label="Pageviews autenticados" value={fmtNum(auth.pageViewsPeriod)} />
            </div>
            {(auth.rolesLastDay || []).length > 0 && (
              <p className="text-xs text-brand-dark/55">
                Roles último día:{" "}
                {auth.rolesLastDay.map((r) => `${r.role} (${r.count})`).join(" · ")}
              </p>
            )}
          </section>

          <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
            <h3 className="font-bold text-lg">Days of period</h3>
            <div className="space-y-3">
              {(traffic.daily || []).map((d) => (
                <div key={d.date} className="space-y-1">
                  <p className="text-[11px] text-brand-dark/55">
                    {d.date} · públicas {fmtNum(d.visitsPublic)} · DAU {fmtNum(d.uniqueAuthUsers)} ·
                    pageviews auth {fmtNum(d.visitsAuth)}
                  </p>
                  <MetricBar label="Visitas públicas" value={d.visitsPublic} max={chartMax} />
                  <MetricBar
                    label="Usuarios que ingresaron"
                    value={d.uniqueAuthUsers}
                    max={chartMax}
                    tone="bg-brand-dark"
                  />
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default function CmsInformesPage() {
  const [tab, setTab] = useState("trafico");
  const [data, setData] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [academyTraffic, setAcademyTraffic] = useState(null);
  const [range, setRange] = useState(() => defaultTrafficRange());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (trafficRange = range) => {
    setLoading(true);
    setError("");
    try {
      const opFn = httpsCallable(functions, "getOperationalInforme");
      const trFn = httpsCallable(functions, "getWebsiteTrafficInforme");
      const acFn = httpsCallable(functions, "getAcademyTrafficInforme");
      const [opResult, trResult, acResult] = await Promise.all([
        opFn({}),
        trFn({ from: trafficRange.from, to: trafficRange.to }).catch((err) => ({
          __trafficError: err,
        })),
        acFn({ from: trafficRange.from, to: trafficRange.to }).catch((err) => ({
          __trafficError: err,
        })),
      ]);
      setData(opResult.data);
      if (trResult?.__trafficError) {
        setTraffic(null);
        const msg = trResult.__trafficError?.message || "";
        if (!String(msg).includes("not-found") && !String(msg).includes("NOT_FOUND")) {
          console.warn("[Informes] tráfico web:", trResult.__trafficError);
        }
      } else {
        setTraffic(trResult.data);
      }
      if (acResult?.__trafficError) {
        setAcademyTraffic(null);
        const msg = acResult.__trafficError?.message || "";
        if (!String(msg).includes("not-found") && !String(msg).includes("NOT_FOUND")) {
          console.warn("[Informes] tráfico academia:", acResult.__trafficError);
        }
      } else {
        setAcademyTraffic(acResult.data);
      }
    } catch (err) {
      setData(null);
      setTraffic(null);
      setAcademyTraffic(null);
      const msg = err?.message || "No se pudo cargar el informe.";
      setError(msg.replace(/^Firebase:\s*/i, "").replace(/\s*\(.*\)\s*$/, "") || msg);
    } finally {
      setLoading(false);
    }
  }, [range]);

  const applyTrafficRange = useCallback(
    (nextRange) => {
      const r = nextRange || range;
      setRange(r);
      load(r);
    },
    [range, load]
  );

  useEffect(() => {
    load(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand-dark text-brand-accent flex items-center justify-center">
              <BarChart3 size={20} />
            </div>
            <div>
              <h2 className="font-display text-3xl font-black">Informes AGDYSA</h2>
              <p className="text-sm text-brand-dark/55 mt-0.5">
                Tráfico Website/Academia · actividad operativa Firestore.
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load(range)}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest border border-brand-soft bg-white hover:bg-brand-soft/40 disabled:opacity-60"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Actualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest border transition-colors ${
              tab === id
                ? "bg-brand-dark text-white border-brand-dark"
                : "bg-white text-brand-dark/70 border-brand-soft hover:border-brand-primary/40"
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {loading && !data && !traffic && (
        <p className="text-sm text-brand-dark/50 py-8 text-center">Cargando informes en vivo…</p>
      )}
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          {error}
        </p>
      )}

      {tab === "trafico" && (
        <TrafficReport
          traffic={traffic}
          range={range}
          onRangeChange={setRange}
          onApply={applyTrafficRange}
          loading={loading}
        />
      )}
      {data && tab === "website" && <ProductReport report={data.website} heurKey="website" />}
      {tab === "academia" && (
        <div className="space-y-8">
          <AcademyTrafficBlock
            traffic={academyTraffic}
            range={range}
            onRangeChange={setRange}
            onApply={applyTrafficRange}
            loading={loading}
          />
          {data?.academia && <ProductReport report={data.academia} heurKey="academia" />}
        </div>
      )}
      {data && tab === "medicina" && <ProductReport report={data.medicina} heurKey="medicina" />}
      {data && tab === "consolidado" && <ConsolidadoView data={data} />}
    </div>
  );
}


